import axios from 'axios'

export interface LoyaltyPoints {
  user_id: string
  total_points: number
  current_tier: string
  next_tier: string | null
  points_to_next_tier: number
  tier_progress: number
  lifetime_points: number
}

export interface LoyaltyTier {
  tier_id: string
  name: string
  min_points: number
  max_points: number | null
  benefits: string[]
  multiplier: number
  color: string
}

export interface LoyaltyTransaction {
  transaction_id: string
  user_id: string
  points: number
  type: 'earned' | 'redeemed' | 'expired' | 'adjusted'
  description: string
  reference_type: string | null
  reference_id: string | null
  created_at: string
}

export interface Reward {
  reward_id: string
  name: string
  description: string
  points_cost: number
  image_url: string | null
  is_available: boolean
  quantity_remaining: number | null
  category: string | null
}

export interface LoyaltyConfig {
  config_id: string
  points_per_currency: number
  bonus_points: Record<string, number>
  tier_upgrade_thresholds: Record<string, number>
  created_at: string
  updated_at: string
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  total: number
}

const API_BASE_URL = '/api'

const loyaltyApi = axios.create({
  baseURL: `${API_BASE_URL}/loyalty`,
  headers: {
    'Content-Type': 'application/json',
  },
})

loyaltyApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

loyaltyApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const loyaltyService = {
  // Points & Tiers
  async getPoints(userId: string): Promise<{ success: boolean; data: LoyaltyPoints }> {
    const response = await loyaltyApi.get(`/points/${userId}`)
    return response.data
  },

  async getTransactions(userId: string, page: number = 1): Promise<PaginatedResponse<LoyaltyTransaction>> {
    const response = await loyaltyApi.get(`/transactions/${userId}?page=${page}&limit=20`)
    return response.data
  },

  async getTiers(): Promise<{ success: boolean; data: LoyaltyTier[] }> {
    const response = await loyaltyApi.get('/tiers')
    return response.data
  },

  async updateTier(tierId: string, data: Partial<LoyaltyTier>): Promise<{ success: boolean; data: LoyaltyTier }> {
    const response = await loyaltyApi.put(`/tiers/${tierId}`, data)
    return response.data
  },

  async createTier(data: Omit<LoyaltyTier, 'tier_id'>): Promise<{ success: boolean; data: LoyaltyTier }> {
    const response = await loyaltyApi.post('/tiers', data)
    return response.data
  },

  async deleteTier(tierId: string): Promise<{ success: boolean; message: string }> {
    const response = await loyaltyApi.delete(`/tiers/${tierId}`)
    return response.data
  },

  // Rewards
  async getRewards(): Promise<{ success: boolean; data: Reward[] }> {
    const response = await loyaltyApi.get('/rewards')
    return response.data
  },

  async createReward(data: Omit<Reward, 'reward_id'>): Promise<{ success: boolean; data: Reward }> {
    const response = await loyaltyApi.post('/rewards', data)
    return response.data
  },

  async updateReward(rewardId: string, data: Partial<Reward>): Promise<{ success: boolean; data: Reward }> {
    const response = await loyaltyApi.put(`/rewards/${rewardId}`, data)
    return response.data
  },

  async deleteReward(rewardId: string): Promise<{ success: boolean; message: string }> {
    const response = await loyaltyApi.delete(`/rewards/${rewardId}`)
    return response.data
  },

  // Configuration
  async getConfig(): Promise<{ success: boolean; data: LoyaltyConfig }> {
    const response = await loyaltyApi.get('/config')
    return response.data
  },

  async updateConfig(data: Partial<LoyaltyConfig>): Promise<{ success: boolean; data: LoyaltyConfig }> {
    const response = await loyaltyApi.put('/config', data)
    return response.data
  },

  // Reports
  async getPointsSummary(): Promise<{ success: boolean; data: { total_points_earned: number; total_points_redeemed: number; total_points_burned: number; active_users: number } }> {
    const response = await loyaltyApi.get('/reports/summary')
    return response.data
  },

  async getTierDistribution(): Promise<{ success: boolean; data: { tier: string; count: number; percentage: number }[] }> {
    const response = await loyaltyApi.get('/reports/tier-distribution')
    return response.data
  },

  // Manual adjustment
  async adjustPoints(userId: string, points: number, reason: string): Promise<{ success: boolean; message: string; data: LoyaltyTransaction }> {
    const response = await loyaltyApi.post('/points/adjust', { user_id: userId, points, reason })
    return response.data
  },
}

export default loyaltyService
